import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchDashboardStats, fetchInspections } from "../Reducers/actions";
import "./widget.css";

const ZwembadinspectiesWidget = () => {
  const dispatch = useDispatch();
  const { dashboardStats, inspections, loading } = useSelector(state => state.zwembadinspecties);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchInspections({ limit: 5 }));
  }, [dispatch]);

  const stats = dashboardStats || {};
  const recentInspections = inspections?.slice(0, 5) || [];

  // Calculate total for percentage
  const total = (stats.in_progress || 0) + (stats.draft || 0) + (stats.completed || 0);

  return (
    <div className="zwembadinspecties-widget">
      {/* Widget Header */}
      <div className="widget-header mb-4">
        <div className="widget-header-content">
          <h5 className="widget-main-title">
            <i className="fas fa-swimming-pool me-2"></i>
            Zwembadinspecties
          </h5>
          <p className="widget-description">Overzicht van uw inspecties</p>
        </div>
        <Link to="/zwembadinspecties/dashboard" className="widget-view-all">
          Dashboard
          <i className="fas fa-arrow-right ms-2"></i>
        </Link>
      </div>

      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="stat-card stat-in-progress">
            <div className="stat-icon">
              <i className="fas fa-spinner"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.in_progress || 0}</div>
              <div className="stat-label">In uitvoering</div>
              {total > 0 && (
                <div className="stat-progress">
                  <div
                    className="stat-progress-bar stat-progress-warning"
                    style={{ width: `${((stats.in_progress || 0) / total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="stat-card stat-draft">
            <div className="stat-icon">
              <i className="fas fa-file-alt"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.draft || 0}</div>
              <div className="stat-label">Concept</div>
              {total > 0 && (
                <div className="stat-progress">
                  <div
                    className="stat-progress-bar stat-progress-gray"
                    style={{ width: `${((stats.draft || 0) / total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="stat-card stat-completed">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.completed || 0}</div>
              <div className="stat-label">Voltooid</div>
              {total > 0 && (
                <div className="stat-progress">
                  <div
                    className="stat-progress-bar stat-progress-success"
                    style={{ width: `${((stats.completed || 0) / total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <h6 className="widget-subtitle mb-3">
            <i className="fas fa-bolt"></i>
            Snelle Acties
          </h6>
          <div className="quick-actions">
            <Link to="/zwembadinspecties/inspecties/nieuw" className="quick-action-btn btn-new">
              <div className="quick-action-icon-wrapper">
                <i className="fas fa-plus-circle"></i>
              </div>
              <span>Nieuwe Inspectie</span>
            </Link>
            <Link to="/zwembadinspecties/inspecties" className="quick-action-btn btn-list">
              <div className="quick-action-icon-wrapper">
                <i className="fas fa-clipboard-list"></i>
              </div>
              <span>Alle Inspecties</span>
            </Link>
            <Link to="/zwembadinspecties/zwembaden" className="quick-action-btn btn-pools">
              <div className="quick-action-icon-wrapper">
                <i className="fas fa-swimming-pool"></i>
              </div>
              <span>Zwembaden</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Inspections */}
      <div className="row">
        <div className="col-12">
          <div className="inspection-header mb-3">
            <h6 className="widget-subtitle">
              <i className="fas fa-history"></i>
              Recente Inspecties
            </h6>
            {!loading && recentInspections.length > 0 && (
              <Link to="/zwembadinspecties/inspecties" className="inspection-view-all">
                Alles bekijken
                <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              <p>Inspecties laden...</p>
            </div>
          ) : recentInspections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <i className="fas fa-clipboard-check"></i>
              </div>
              <p className="empty-state-title">Geen inspecties gevonden</p>
              <p className="empty-state-subtitle">Start met het aanmaken van een nieuwe inspectie</p>
              <Link to="/zwembadinspecties/inspecties/nieuw" className="empty-state-btn">
                <i className="fas fa-plus-circle me-2"></i>
                Nieuwe Inspectie
              </Link>
            </div>
          ) : (
            <div className="inspection-list">
              {recentInspections.map((inspection, index) => (
                <Link
                  key={inspection._id}
                  to={`/zwembadinspecties/inspecties/${inspection._id}/uitvoeren`}
                  className="inspection-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="inspection-icon">
                    <i className={`fas ${getStatusIcon(inspection.status)}`}></i>
                  </div>
                  <div className="inspection-content">
                    <div className="inspection-title">
                      {inspection.title || inspection.company_id?.name || 'Onbekend zwembad'}
                    </div>
                    <div className="inspection-meta">
                      {inspection.title && (
                        <span className="inspection-location">
                          <i className="fas fa-map-marker-alt"></i>
                          {inspection.company_id?.name || 'Onbekend zwembad'}
                        </span>
                      )}
                      <span className="inspection-date">
                        <i className="far fa-calendar-alt"></i>
                        {new Date(inspection.inspection_date).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                      {inspection.template_id?.name && (
                        <span className="inspection-template">
                          <i className="far fa-file-alt"></i>
                          {inspection.template_id.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="inspection-status-wrapper">
                    <span className={`inspection-status status-${inspection.status}`}>
                      {getStatusLabel(inspection.status)}
                    </span>
                    <div className="inspection-arrow">
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusIcon = (status) => {
  switch (status) {
    case 'draft':
      return 'fa-file-alt';
    case 'in_progress':
      return 'fa-spinner';
    case 'completed':
      return 'fa-check-circle';
    case 'approved':
      return 'fa-thumbs-up';
    case 'rejected':
      return 'fa-times-circle';
    default:
      return 'fa-clipboard';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'draft':
      return 'Concept';
    case 'in_progress':
      return 'In uitvoering';
    case 'completed':
      return 'Voltooid';
    case 'approved':
      return 'Goedgekeurd';
    case 'rejected':
      return 'Afgekeurd';
    default:
      return status;
  }
};

export default ZwembadinspectiesWidget;
